import { postOrgProxyConfigsByProxyConfigIdDeactivateMutation } from '@onefootprint/fetch-dashboard';
import { getErrorMessage } from '@onefootprint/request';
import type { ProxyConfigBasic } from '@onefootprint/request-types/dashboard';
import { useToast } from '@onefootprint/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ConfirmationDialog from '@/components/confirmation-dialog';
export type RemoveHandler = {
  remove: () => void;
};
export type RemoveProps = {
  proxyConfig: ProxyConfigBasic;
};
const Remove = forwardRef<RemoveHandler, RemoveProps>(({
  proxyConfig
}, ref) => {
  const {
    t
  } = useTranslation('vault-proxy', {
    keyPrefix: 'actions.remove'
  });
  const {
    t: alertT
  } = useTranslation('vault-proxy', {
    keyPrefix: 'notifications.remove'
  });
  const [open, setOpen] = useState(false);
  const mutation = useMutation(postOrgProxyConfigsByProxyConfigIdDeactivateMutation({
    path: {
      proxyConfigId: proxyConfig.id
    }
  }));
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const remove = () => {
    mutation.mutate({
      path: {
        proxyConfigId: proxyConfig.id
      }
    }, {
      onError: (error: unknown) => {
        toast({
          description: getErrorMessage(error),
          title: alertT($ => $.error.title),
          variant: 'error'
        });
      },
      onSuccess: () => {
        toast({
          description: alertT($ => $.success.description, {
            name: proxyConfig.name
          }),
          title: alertT($ => $.success.title)
        });
        queryClient.invalidateQueries();
      }
    });
  };
  const handleRemove = () => {
    setOpen(true);
  };
  useImperativeHandle(ref, () => ({
    remove: handleRemove
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  return <ConfirmationDialog isPending={mutation.isPending} onClose={() => setOpen(false)} onConfirm={remove} open={open} title={t($ => $.confirmation.title)}>
      <Trans ns="vault-proxy" i18nKey={$ => $.actions.remove.confirmation.description} components={{
      b: <b className="font-semibold text-label-2" />
    }} values={{
      name: proxyConfig.name
    }} />
    </ConfirmationDialog>;
});
export default Remove;